import { Callout, Collapse, Intent } from '@blueprintjs/core';
import * as React from 'react';

import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import { PropertyTypeSelector } from 'src/components/PropertyTypeSelector';
import { ProjectPropertyType } from 'src/constants/project';
import { TEST_IDS } from 'src/constants/testIds';
import { useProjectBaseSettings } from 'src/containers/ProjectSettingsDrawer/hooks/useProjectBaseSettingsValue';
import { useProjectSettingsValidation } from 'src/containers/ProjectSettingsDrawer/hooks/useProjectSettingsValidationValue';
import { projectSelector } from 'src/selectors/project';
import { propertyTypeCountSelector } from 'src/selectors/propertyType';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

// warning should be visible when custom property types will be overwritten
// (when going from custom to yes/no and custom values don't match selection)
export const isPropertyTypeWarningVisible = (
  projectPropertyType: ProjectPropertyType | undefined,
  newPropertyType: ProjectPropertyType,
  isAllPropertyType: ReturnType<
    ReturnType<typeof propertyTypeCountSelector>
  >['isAllPropertyType']
) =>
  newPropertyType !== ProjectPropertyType.CUSTOM &&
  projectPropertyType === ProjectPropertyType.CUSTOM &&
  !isAllPropertyType[newPropertyType];

export const ProjectSettingsDrawerAdvancedDetails: React.VFC = () => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const { isAllPropertyType } = useReduxSelector(
    propertyTypeCountSelector(projectId)
  );

  const { dataNamespace, propertyType, setDataNamespace, setPropertyType } =
    useProjectBaseSettings();

  const { withValidation } = useProjectSettingsValidation();

  return (
    <div className="sd-advanced-project-details">
      <InlineEditableLabel
        label={copy.components.projectSettingsDrawer.dataNamespace}
        onSetValue={setDataNamespace}
        onValidate={withValidation('namespace')}
        placeholder={project?.dataNamespace || ''}
        testId={TEST_IDS.projectSettingsDrawer.dataNamespace}
        value={dataNamespace}
      />
      <PropertyTypeSelector
        allowCustom
        propertyType={propertyType}
        setPropertyType={setPropertyType}
      />
      <Collapse
        isOpen={isPropertyTypeWarningVisible(
          project?.propertyType,
          propertyType,
          isAllPropertyType
        )}
      >
        <Callout
          data-testid={TEST_IDS.projectSettingsDrawer.customPropertyTypeWarning}
          intent={Intent.WARNING}
        >
          {copy.components.projectSettingsDrawer.customPropertyTypeWarning}
        </Callout>
      </Collapse>
    </div>
  );
};
